import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, SettingsIcon, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as AvatarIcon } from "../../../assets/icons/logo-88.svg";
import AppLogo from "../../ui/AppLogo";
import { NavItem } from "./superAdminNavLinkData";

interface SidebarProps {
  allowedNavItems: NavItem[];
  isExpanded: boolean;
  isMobile?: boolean;
  onMobileClose?: () => void;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  activeNav: string;
  activeSubNav: string | null;
  openDropdowns: Set<string>;
  handleSubNavClick: (subNavId: string) => void;
  email: string;
  handleNavClick: (navId: string, hasSubItems: boolean) => void;
}

const SuperAdminSidebar: React.FC<SidebarProps> = ({
  allowedNavItems,
  isExpanded,
  isMobile = false,
  onMobileClose,
  setIsExpanded,
  activeNav,
  activeSubNav,
  openDropdowns,
  handleSubNavClick,
  email,
  handleNavClick,
}) => {
  const navigate = useNavigate();
  return (
    <motion.aside
      initial={false}
      animate={{ width: isExpanded ? 280 : 80 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="bg-white text-white flex flex-col relative border-r border-gray-300 mx-auto h-full max-w-[85vw]"
    >
      {isMobile && (
        <button
          type="button"
          onClick={onMobileClose}
          aria-label="Close navigation menu"
          className="absolute right-3 top-3 z-10 rounded-full p-2 text-hColor hover:bg-gray-100"
        >
          <X size={20} />
        </button>
      )}

      {/* Logo */}
      <div className="px-6 py-12 w-full">
        <motion.div
          animate={{ opacity: isExpanded ? 1 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex items-center space-x-3"
        >
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <AppLogo className="w-[226px] h-[41px] object-cover" />
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* User Info */}
      <div className="p-4">
        <motion.div
          animate={{ opacity: isExpanded ? 1 : 0 }}
          className="flex items-center space-x-3 px-2 py-4"
        >
          <div className="w-10 h-10 bg-[#EDF2DFB2] p-2 rounded-full flex items-center justify-center">
            <AvatarIcon className="w-full h-full" />
          </div>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="flex-1 min-w-0"
            >
              <p className="text-[16px] text-hColor font-semibold truncate">
                Mangate Ltd
              </p>
              <p className="text-[14px] text-pColor truncate">{email}</p>
            </motion.div>
          )}
          <div className="text-[#BDCC87]">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
            >
              <path
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="m17 14l-5-5l-5 5"
              />
            </svg>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
            >
              <path
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="m7 10l5 5l5-5"
              />
            </svg>
          </div>
        </motion.div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-6 overflow-y-auto">
        <ul className="space-y-4 px-8">
          {allowedNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeNav === item.id;
            const hasSubItems = item.subItems && item.subItems.length > 0;
            const isDropdownOpen = openDropdowns.has(item.id);

            return (
              <li key={item.id}>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleNavClick(item.id, hasSubItems || false)}
                  className={`w-[216px] flex items-center justify-between px-2 py-3 rounded-lg transition-all duration-200 ${
                    isActive
                      ? "text-primary font-semibold"
                      : "text-pColor hover:text-primary font-medium"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="">
                      <Icon
                        className={`w-[24px] h-[24px] ${
                          isActive ? "text-primary shadow-lg" : "text-pColor"
                        }`}
                      />
                    </div>
                    {isExpanded && (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.1 }}
                        className="text-[18px]"
                      >
                        {item.name}
                      </motion.span>
                    )}
                  </div>
                  {isExpanded && hasSubItems && (
                    <motion.div
                      animate={{ rotate: isDropdownOpen ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown size={16} />
                    </motion.div>
                  )}
                </motion.button>

                {/* Sub Navigation */}
                <AnimatePresence>
                  {isExpanded && hasSubItems && isDropdownOpen && (
                    <motion.ul
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="ml-8 mt-2 space-y-1 overflow-hidden"
                    >
                      {item.subItems?.map((subItem) => {
                        const SubIcon = subItem.icon;
                        const isSubActive = activeSubNav === subItem.id;

                        return (
                          <motion.li
                            key={subItem.id}
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.1 }}
                          >
                            <button
                              onClick={() => handleSubNavClick(subItem.id)}
                              className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm transition-all duration-200 ${
                                isSubActive
                                  ? "text-primary border-l-2"
                                  : "text-gray-400 hover:text-primary"
                              }`}
                            >
                              {SubIcon && <SubIcon size={16} />}
                              <span>{subItem.name}</span>
                            </button>
                          </motion.li>
                        );
                      })}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </li>
            );
          })}

          <li
            onClick={() => navigate("/super-admin/settings")}
            className="cursor-pointer w-[216px] flex items-center justify-between px-2 py-3 border border-gray-100 rounded-lg transition-all duration-200"
          >
            <motion.div
              animate={{ opacity: isExpanded ? 1 : 0 }}
              className="flex items-center text-pColor space-x-3"
            >
              <SettingsIcon size={24} />
              <p className="text-[16px]">Profile settings</p>
            </motion.div>
          </li>
        </ul>
      </nav>

      {/* Toggle Button */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsExpanded(!isExpanded)}
        className={`${isMobile ? "hidden" : ""} absolute -right-4 top-20 bg-primary hover:bg-primary/80 text-white p-2 rounded-full shadow-lg transition-colors duration-200`}
      >
        {isExpanded ? <X size={16} /> : <Menu size={16} />}
      </motion.button>
    </motion.aside>
  );
};

export default SuperAdminSidebar;
